import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Signature } from "@phosphor-icons/react";

// The drawn image decorates certificates the user releases (issue #73); the
// electronic signature itself stays the credential-executed record. Kept
// self-service on purpose — nobody draws another person's hand.
const WIDTH = 480;
const HEIGHT = 140;

export default function SignaturePad() {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const [saved, setSaved] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me/signature");
      setSaved(data.image_b64 || null);
    } catch {
      // The pad still works for drawing; saving will surface real errors.
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const context = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0F172A";
    return ctx;
  };

  const position = (event) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: ((event.clientX - rect.left) / rect.width) * WIDTH,
      y: ((event.clientY - rect.top) / rect.height) * HEIGHT,
    };
  };

  const start = (event) => {
    event.preventDefault();
    canvasRef.current.setPointerCapture(event.pointerId);
    drawingRef.current = true;
    const { x, y } = position(event);
    const ctx = context();
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const move = (event) => {
    if (!drawingRef.current) return;
    const { x, y } = position(event);
    const ctx = context();
    ctx.lineTo(x, y);
    ctx.stroke();
    setDirty(true);
  };

  const end = () => { drawingRef.current = false; };

  const clearCanvas = () => {
    const ctx = context();
    ctx.clearRect(0, 0, WIDTH, HEIGHT);
    setDirty(false);
  };

  const save = async () => {
    setBusy(true);
    try {
      const dataUrl = canvasRef.current.toDataURL("image/png");
      await api.put("/auth/me/signature", { image_b64: dataUrl.split(",")[1] });
      toast.success("Signature saved");
      clearCanvas();
      await load();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail) || error.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete("/auth/me/signature");
      toast.success("Signature removed");
      setSaved(null);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail) || error.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="signature-pad">
      <div className="flex items-center gap-2">
        <Signature size={18} className="text-slate-400" />
        <h2 className="font-medium">My signature</h2>
      </div>
      <p className="mt-1 text-xs text-slate-400">
        Shown on certificates you release, above your printed name. Your
        electronic signature is still executed with your credentials — this
        image only accompanies it.
      </p>
      {saved && (
        <div className="mt-4 flex items-center gap-4">
          <img src={`data:image/png;base64,${saved}`} alt="Stored signature"
               className="h-14 rounded border border-slate-200 bg-white px-3 py-1" />
          <button onClick={remove} disabled={busy}
                  className="rounded border border-slate-200 px-3 py-1.5 text-xs hover:border-slate-400">
            Remove
          </button>
        </div>
      )}
      <div className="mt-4">
        <canvas
          ref={canvasRef}
          width={WIDTH}
          height={HEIGHT}
          data-testid="signature-canvas"
          className="w-full max-w-md touch-none rounded-md border border-dashed border-slate-300 bg-slate-50"
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerLeave={end}
        />
        <div className="mt-2 flex gap-2">
          <button onClick={save} disabled={!dirty || busy}
                  className="rounded-md bg-slate-900 px-3 py-1.5 text-xs text-white disabled:opacity-40">
            {saved ? "Replace signature" : "Save signature"}
          </button>
          <button onClick={clearCanvas} disabled={!dirty || busy}
                  className="rounded border border-slate-200 px-3 py-1.5 text-xs hover:border-slate-400">
            Clear
          </button>
        </div>
      </div>
    </div>
  );
}
